import React, { useEffect, useState } from "react";
import {
    createAssessment,
    deleteQuestion,
    getAssessmentDetails,
    getVersions,
    publishAssessment,
    updateAssessmentDetails,
} from "../../../../services/svt/svt-admin/assessment-management.service";

import IconButton from "../../IconButton/IconButton";
import Loader from "../../../common/Loader/Loader";
import ModalComponent from "../../../common/ModalComponent/ModalComponent";
import PlusIcon from "../../../../assets/PlusIcon";
import { toast } from "react-toastify";
import validateFields from "../../../../utils/ValidationInput";
import { DataTableComponent } from "../../../common/TableComponent/DataTableComponent";
import { useTranslation } from "react-i18next";
import i18n from './../../../../utils/i18n';

const AssessmentView = ({
    setActiveTab,
    assessmentdetails,
    setAssessmentDetails,
    modalMode,
    setModalMode,
    slotData,
    isAddQueModalOpen,
    levelsList,
    setIsAddQueModalOpen,
    assessmentPayload,
    tableRowData,
}) => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [isModalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState(0);
    const [versionList, setVersionList] = useState([]);
    const [isPublished, setIsPublished] = useState(false);
    const [viewModified, setViewModified] = useState(false);
    useEffect(() => {
        if (assessmentdetails && assessmentdetails.length > 0 && !version) {
            fetchVersionList();
        }
    }, [assessmentdetails, setAssessmentDetails]);

    useEffect(() => {
        getVersionInfo();
    }, [version, assessmentdetails, i18n.language]);

    const getVersionInfo = () => {
        const index = assessmentdetails.findIndex(
            (item) => item.version === version
        );
        if (index !== -1) fetchData(index);
        const isPublished = versionList.find(
            (item) => item.version === version && item.isPublished === true
        );
        setIsPublished(isPublished);
    };

    useEffect(() => {
        if (isAddQueModalOpen === true) {
            openModal("", "", "add");
            setLoading(false);
        }
    }, [isAddQueModalOpen]);

    const fetchVersionList = async () => {
        const assessmentId = assessmentdetails[0]?.templateMasterId;
        const languageId = assessmentdetails[0]?.langId;
        try {
            setLoading(true);
            const res = await getVersions(assessmentId, languageId);
            setVersionList(res.data);
            res.data.length > 0 && setVersion(assessmentdetails[0].version);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = (index) => {
        const assessmentData = assessmentdetails[index].questions.map(
            (item) => ({
                id: item.id,
                level: t(item.level.toLocaleLowerCase()),
                questionText: item.questionText,
                questionType: t(item.questionType),
                option1: t(item.options[1]),
                option2: t(item.options[2]),
                scoringOption: item.scoringOption,
                evidence: t(item.evidence),
                hitachiGuidance: item.hitachiGuidance,
            })
        );
        setData(assessmentData);
    };
    const rowsPerPage = 10;

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentRows = data.slice(startIndex, endIndex);

    const handleNext = () => {
        if (endIndex < data.length) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handleBack = () => {
        if (currentPage > 0) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    const handleUpdate = async () => {
        const questionMax = 500;
        const guidanceMax = 8000;

        if (formData.question.length > questionMax) {
            toast.error(t("questionExceedsMaxLength", { number: 500 }), { position: "top-center", theme: "dark" });
            return false;
        }
        if (formData.guidance && formData.guidance.length > guidanceMax) {
            toast.error(t("hitachiGuidanceExceedsMaxLength", { number: 8000 }), { position: "top-center", theme: "dark" });
            return false;
        }
        console.log(formData.questionType);
        const assessmentId = assessmentdetails[0]?.id;
        const languageId = assessmentdetails[0]?.langId;
        const updateData = {
            languageId: languageId,
            updateQuestions: [
                {
                    id: formData.id,
                    text: formData.question,
                    option1: formData.option1,
                    option2: formData.option2,
                    questionTypeId: formData.questionType === "YesNo" ? 1 : 0,
                },
            ],
        };
        try {
            const response = await updateAssessmentDetails(
                assessmentId,
                updateData
            );
            if (response) {
                setModalOpen(false);
                fetchAssessmentDetails(
                    assessmentdetails[0]?.templateMasterId,
                    languageId
                );
                toast.success(t("questionUpdatedSuccessfully"));
                setViewModified(true);
            }
        } catch (error) {
            console.error("Error updating assessment:", error);
        }
    };

    const fetchAssessmentDetails = async (assessmentMasterId, langId) => {
        try {
            setLoading(true);
            const res = await getAssessmentDetails(assessmentMasterId, langId);
            setAssessmentDetails(res.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        setLoading(true);
        const assessmentId = assessmentdetails[0]?.templateMasterId;
        const languageId = assessmentdetails[0]?.langId;
        const publishData = {
            templateMasterId: assessmentId,
            languageId: languageId,
            slotId: formData.slot,
            version: version,
        };
        try {
            const response = await publishAssessment(publishData);
            if (response) {
                setModalOpen(false);
                setActiveTab("Tab1");
                setLoading(false);
                setAssessmentDetails([]);
                setVersionList([]);
            }
        } catch (error) {
            console.error("Error publishing assessment:", error);
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        const assessmentId = assessmentdetails[0]?.templateMasterId;
        const languageId = assessmentdetails[0]?.langId;
        try {
            const response = await deleteQuestion(formData.id);
            if (response) {
                setModalOpen(false);
                fetchAssessmentDetails(assessmentId, languageId);
                setLoading(false);
                toast.warn(t("questionDeletedSuccessfully"));
                setViewModified(true);
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            setLoading(false);
        }
    };

    const trimText = (text) => {
        return text.trim().replace(/\s+/g, " ");
    };

    const addQuestionWithAssessment = async () => {
        setLoading(true);
        let maxCharLength = 255;

        if (!trimText(formData.question)) {
            toast.error(t('missingRequiredFieldQuestion'), {
                position: "top-center",
                theme: "dark",
            });
            setLoading(false);
            return false;
        }
        // Check max length
        if (formData.question.length > 500) {
            toast.error(
                t("questionExceedsMaxLength", { maxLength: 500 }),
                { position: "top-center", theme: "dark" }
            );
            setLoading(false);
            return false;
        }
        // Check max length
        if (formData.guidance.length > 8000) {
            toast.error(
                t("hitachiGuidanceExceedsMaxLength", { maxLength: 8000 }),
                { position: "top-center", theme: "dark" }
            );
            setLoading(false);
            return false;
        }

        let payload = {
            templateMasterId: assessmentPayload?.templateMasterId,
            languageId: assessmentPayload?.languageId,
            questions: [
                {
                    text: trimText(formData.question),
                    levelId: parseInt(formData.level),
                    questionTypeId: formData.questionType === "YesNo" ? 1 : 0,
                    option1: formData.option1,
                    option2: formData.option2,
                    scoringOption: formData.scoringOption === "Yes" ? "1" : "2",
                    isEvidenceRequired:
                        formData.evidence === "Yes" ? true : false,
                    resource: formData.guidance,
                },
            ],
        };

        await createAssessment(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                setModalOpen(false);
                fetchAssessmentDetails(
                    assessmentPayload?.templateMasterId,
                    assessmentPayload?.languageId
                );
                setLoading(false);
                // toast.success(t("questionAddedSuccessfully"));
                setViewModified(true);
                setIsAddQueModalOpen(false);
            }
        });
    };

    const handleAdd = async () => {
        setLoading(true);
        if (!trimText(formData.question)) {
            toast.error(t('missingRequiredFieldQuestion'), {
                position: "top-center",
                theme: "dark",
            });
            setLoading(false);
            return false;
        }

        console.log(formData.questionType, formData);

        const assessmentId = getIdByVersion(assessmentdetails, version);
        const languageId = assessmentdetails[0]?.langId;
        const updateData = {
            languageId: languageId,
            updateQuestions: [
                {
                    text: trimText(formData.question),
                    levelId: parseInt(formData.level),
                    questionTypeId: formData.questionType === "YesNo" ? 1 : 0,
                    option1: formData.option1,
                    option2: formData.option2,
                    scoringOption: formData.scoringOption === "Yes" ? "1" : "2",
                    isEvidenceRequired:
                        formData.evidence === "Yes" ? true : false,
                    resource: formData.guidance,
                },
            ],
        };
        try {
            const response = await updateAssessmentDetails(
                assessmentId,
                updateData
            );
            if (response) {
                setModalOpen(false);
                fetchAssessmentDetails(
                    assessmentdetails[0]?.templateMasterId,
                    languageId
                );
                setLoading(false);
                toast.success(t("questionAddedSuccessfully"));
                setViewModified(true);
            }
        } catch (error) {
            console.error("Error updating assessment:", error);
            setLoading(false);
        }
    };

    const openModal = (row, rowIndex, mode) => {
        setModalMode(mode || "edit");
        if (mode === "publish") {
            let activeSlots = slotData?.filter((item) => item?.isActive === true);
            
            setFormData({
                slot: activeSlots.length !== 0 ? activeSlots[0]?.id : '',
                version: assessmentdetails[0]?.version,
            });
        } else if (mode === "add") {
            setFormData({
                templateName:
                    isAddQueModalOpen === true
                        ? assessmentPayload?.selectedTempName
                        : assessmentdetails[0]?.assessmentName,
                level: levelsList[0].id,
                questionType: t("YesNo"),
                question: "",
                option1: t("yes"),
                option2: t("no"),
                scoringOption: t("yes"),
                evidence: t("no"),
                guidance: "",
            });
        } else if (mode === "edit") {
            setFormData({
                id: row.id,
                question: row.questionText,
                questionType: t("YesNo"),
                option1: t("yes"),
                option2: t("no"),
            });
        } else if (mode === "delete") {
            setFormData({
                id: row.id,
            });
        } else {
            setFormData({});
        }
        setModalOpen(true);
    };

    const columns = [
        { name: t("level"), field: "level", selector: (row) => row.level, sortable: true },
        { name: t("question"), field: "questionText", selector: (row) => row.questionText, sortable: true, width: "250px" },
        { name: t("questionType"), field: "questionType", selector: (row) => row.questionType, sortable: true },
        { name: t("option1"), field: "option1", selector: (row) => row.option1, sortable: true },
        { name: t("option2"), field: "option2", selector: (row) => row.option2, sortable: true },
        { name: t("scoringOption"), field: "scoringOption", selector: (row) => row.scoringOption, sortable: true },
        { name: t("evidenceRequired"), field: "evidence", selector: (row) => row.evidence, sortable: true },
        { name: t("hitachiGuidance"), field: "hitachiGuidance", selector: (row) => row.hitachiGuidance, sortable: true, width: "400px" },
    ];

    const getFieldsData = () => {
        if (modalMode === "add") {
            return [
                {
                    fieldName: t("templateName"),
                    type: "input",
                    name: "templateName",
                    readOnly: true,
                },
                {
                    fieldName: t("level"),
                    type: "dropdown",
                    name: "level",
                    options: levelsList,
                },
                {
                    fieldName: t("questionType"),
                    type: "dropdown",
                    name: "questionType",
                    options: [
                        { label: t("yesNo"), value: "Yes/No" },
                    ],
                },
                { fieldName: t("question"), type: "textarea", name: "question", required: true },
                {
                    fieldName: t("option1"),
                    type: "input",
                    name: "option1",
                    readOnly: true,
                },
                {
                    fieldName: t("option2"),
                    type: "input",
                    name: "option2",
                    readOnly: true,
                },
                {
                    fieldName: t("scoringOption"),
                    type: "dropdown",
                    name: "scoringOption",
                    options: [
                        { label: t("yes"), value: "1" },
                        { label: t("no"), value: "2" },
                    ],
                },
                {
                    fieldName: t("evidence"),
                    type: "dropdown",
                    name: "evidence",
                    options: [
                        { label: t("optional"), value: "No" },
                        { label: t("required"), value: "Yes" },
                    ],
                },
                {
                    fieldName: t("hitachiGuidance"),
                    type: "input",
                    name: "guidance",
                },
            ];
        } else if (modalMode === "publish") {
            return [
                {
                    fieldName: t("selectSlot"),
                    type: "dropdown",
                    name: "slot",
                    options: slotData
                        ?.filter((item) => item.isActive === true)
                        ?.map((slot) => ({
                            label: slot.name,
                            value: slot.id,
                        })),
                },
            ];
        } else if (modalMode === "edit") {
            return [
                { fieldName: t("question"), type: "textarea", name: "question" },
            ];
        } else if (modalMode === "delete") {
            return [
                {
                    text: t("deleteQuestionConfirm"),
                    name: "id",
                },
            ];
        }
        return [];
    };

    const handleInputChange = (target, field) => {
        const { name, value } = target;
        toast.dismiss();
        if (name === "question" && value.length > 500) {
            toast.error(t("questionExceedsMaxLength", { number: 500 }), { position: "top-center" });
            return;
        }
        if (name === "guidance" && value.length > 8000) {
            toast.error(t("hitachiGuidanceExceedsMaxLength", { number: 8000 }), { position: "top-center" });
            return;
        }
        setFormData((prev) => ({
            ...prev,
            [target.name]: target.value,
        }));
    };

    const onCloseModal = (modalMode) => {
        if (modalMode === "add") {
            setFormData({
                templateName: "",
                level: "",
                questionType: "Yes/No",
                question: "",
                option1: "Yes",
                option2: "No",
                scoringOption: "Yes",
                evidence: "No",
                guidance: "",
            });
        }
        setModalOpen(false);
        if (isAddQueModalOpen === true) {
            setActiveTab("Tab1");
        }
        setIsAddQueModalOpen(false);
    };

    const getIdByVersion = (data, version) => {
        const item = data.find((entry) => entry.version === version);
        return item ? item.id : null;
    };

    return loading ? (
        <Loader />
    ) : (
        <div className="assessmentTable-outer">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <span
                    className="assessmentTableView-header"
                    style={{ marginRight: "auto" }}
                >
                    {t("assessmentNameAndLanguage", {
                        assessmentName: assessmentdetails[0]?.assessmentName,
                        langName: assessmentdetails[0]?.langName
                    })}
                </span>

                {versionList?.length > 0 && (
                    <select
                        onChange={(e) => {
                            handleBack();
                            setVersion(parseInt(e.target.value));
                        }}
                        value={version}
                    >
                        {versionList.slice().sort((a, b) => a.version - b.version)
                            .map((item, index) => (
                                <option key={index} value={item.version}>
                                    {`${t("version")} ${item.version}.0`}
                                </option>
                            ))}
                    </select>
                )}

                <div
                    className="buttonOuterDiv"
                    style={{ margin: "0px 0px 0px 15px" }}
                >
                    {!isPublished && (
                        <IconButton
                            text={t("question")}
                            onClick={() => openModal("", "", "add")}
                            style={{
                                backgroundColor: "#F0F0F0",
                                color: "#454647",
                                border: "1px solid #B3B2B4",
                            }}
                            icon={<PlusIcon />}
                        />
                    )}
                </div>

                <div style={{ marginLeft: "15px" }}>
                    {!isPublished && data.length > 0 && (
                        <button
                            onClick={() => openModal("", "", "publish")}
                            className="saveButton"
                        >
                            {t("publish")}
                        </button>
                    )}
                </div>
            </div>
            <div style={{ marginTop: "1rem" }}>
                <DataTableComponent
                    columns={columns}
                    data={data}
                    // data={currentRows}
                    tableName="AdminAssessmentTableView"
                    onEditDetails={openModal}
                    onDeleteDetails={openModal}
                    isAssessmentPublished={isPublished}
                    pageCount={10}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: data?.length === 0 ? "1rem" : "-2.4rem",
                }}
            >
                <button
                    className="cancelButton"
                    onClick={() => {
                        setLoading(true);
                        setActiveTab("Tab1");
                        setAssessmentDetails([]);
                    }}
                    style={{ marginRight: "10px" }}
                >
                    {t("cancel")}
                </button>
            </div>
            {isModalOpen && (
                <ModalComponent
                    isOpen={isModalOpen}
                    onClose={() => onCloseModal(modalMode)}
                    modalTitle={
                        modalMode === "publish"
                            ? t("publishAssessment")
                            : modalMode === "create"
                                ? t("createAssessment")
                                : modalMode === "delete"
                                    ? t("deleteQuestion")
                                    : modalMode === "add"
                                        ? t("addQuestion")
                                        : t("updateAssessment")
                    }
                    fieldsData={getFieldsData()}
                    formData={formData}
                    onChangeInput={handleInputChange}
                    buttons={[
                        modalMode === "edit" ? (
                            <button
                                className="saveButton"
                                key="save"
                                onClick={handleUpdate}
                            >
                                {t("update")}
                            </button>
                        ) : modalMode === "publish" ? (
                            <button
                                onClick={() => handlePublish()}
                                className="saveButton"
                                key="publish"
                            >
                                {t("publish")}
                            </button>
                        ) : modalMode === "delete" ? (
                            <button
                                onClick={() => handleDelete()}
                                className="saveButton"
                                key="publish"
                            >
                                {t("delete")}
                            </button>
                        ) : modalMode === "add" ? (
                            <button
                                onClick={() =>
                                    isAddQueModalOpen === true
                                        ? addQuestionWithAssessment()
                                        : handleAdd()
                                }
                                className="saveButton"
                                key="publish"
                            >
                                {t("add")}
                            </button>
                        ) : (
                            <button className="saveButton" key="save">
                                {t("save")}
                            </button>
                        ),
                        <button
                            className="cancelButton"
                            key="cancel"
                            onClick={() =>
                                modalMode === "add"
                                    ? onCloseModal("add")
                                    : setModalOpen(false)
                            }
                        >
                            {t("cancel")}
                        </button>,
                    ]}
                />
            )}
        </div>
    );
};

export default AssessmentView;
